import type { SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import type { InventoryAdjustmentHistorySearchParams } from "../schemas"
import { useInventoryAdjustmentHistorySearchStore } from "../stores/useInventoryAdjustmentHistorySearchStore"

import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseInventoryAdjustmentHistoryListReturn {
  inventoryAdjustmentHistoryItems: AdjustmentHistoryItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: InventoryAdjustmentHistorySearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof InventoryAdjustmentHistorySearchParams>(
    key: K,
    value: InventoryAdjustmentHistorySearchParams[K]
  ) => void
  handleFilterRemove: (key: keyof InventoryAdjustmentHistorySearchParams, value?: string | number) => void
  handleFilterClear: () => void
  handleScroll: () => void
}

const ROUTE = "/app/inventory/_layout/adjustment-history"

const FILTER_KEYS: (keyof InventoryAdjustmentHistorySearchParams)[] = [
  "adjustmentTypes",
  "adjustmentReasons",
  "statuses",
  "warehouseAreaIds",
  "adjustedByIds",
  "dateFrom",
  "dateTo",
]

export const useInventoryAdjustmentHistoryList = (): UseInventoryAdjustmentHistoryListReturn => {
  const store = useInventoryAdjustmentHistorySearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: inventoryAdjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<AdjustmentHistoryItem, InventoryAdjustmentHistorySearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  return {
    inventoryAdjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
